import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { loadToken, loadTokenFromCloud, saveToken, clearToken } from '../lib/tokenStorage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext();

// How often to silently refresh the JWT while the app is open (24 hours).
// With a 90-day server-side expiry, calling refresh every 24h keeps active
// users logged in effectively forever.
const REFRESH_INTERVAL_MS = 24 * 60 * 60 * 1000;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const getTelegram = () => window.Telegram?.WebApp;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setTokenState] = useState(loadToken());
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef(null);

  const setToken = (newToken) => {
    setTokenState(newToken);
    if (newToken) saveToken(newToken);
    else clearToken();
  };

  useEffect(() => {
    initAuth();
    return () => { if (refreshTimerRef.current) clearInterval(refreshTimerRef.current); };
  }, []);

  const initAuth = async () => {
    const tg = getTelegram();

    // 1. Try the token we have locally.
    let workingToken = token;

    // 2. If nothing local, try Telegram CloudStorage (survives WebView cache clears).
    if (!workingToken) {
      workingToken = await loadTokenFromCloud();
      if (workingToken) saveToken(workingToken);  // back-fill localStorage
    }

    // 3. If we have a token, refresh it (sliding window) — this both verifies validity
    //    AND extends its expiry by another 90 days.
    if (workingToken) {
      const refreshed = await tryRefreshToken(workingToken);
      if (refreshed) {
        setLoading(false);
        startBackgroundRefresh();
        return;
      }
      // Refresh failed (token expired / invalidated). Fall through to Telegram auto-auth.
    }

    // 4. Telegram auto-auth — silent, no login screen shown.
    if (tg?.initData && tg.initDataUnsafe?.user) {
      try {
        const response = await axios.post(`${API}/auth/telegram`, {
          telegram_user: tg.initDataUnsafe.user,
          init_data: tg.initData,
        });
        const { token: newToken, user: userData } = response.data;
        setToken(newToken);
        setUser(userData);
        startBackgroundRefresh();
      } catch (error) {
        console.error('Telegram auth failed:', error);
      }
    }

    setLoading(false);
  };

  const tryRefreshToken = async (authToken) => {
    try {
      const res = await axios.post(`${API}/auth/refresh`, null, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const { token: fresh, user: userData } = res.data;
      setToken(fresh);
      setUser(userData);
      return true;
    } catch (err) {
      // Only clear on explicit 401 (token invalid). Network errors leave us signed-in.
      if (err.response?.status === 401) {
        setToken(null);
        setUser(null);
      }
      return false;
    }
  };

  const startBackgroundRefresh = () => {
    if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    refreshTimerRef.current = setInterval(() => {
      const current = loadToken();
      if (current) tryRefreshToken(current);
    }, REFRESH_INTERVAL_MS);
  };

  const fetchUserProfile = async (authToken) => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setUser(response.data.user);
      return true;
    } catch (error) {
      // Only wipe on true auth failure — network hiccups shouldn't log user out.
      if (error.response?.status === 401) {
        setToken(null);
      }
      return false;
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    setToken(newToken);
    setUser(userData);
    startBackgroundRefresh();
    return response.data;
  };

  const register = async (email, username, password) => {
    const response = await axios.post(`${API}/auth/register`, { email, username, password });
    const { token: newToken, user: userData } = response.data;
    setToken(newToken);
    setUser(userData);
    startBackgroundRefresh();
    return response.data;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
  };

  const updateBalance = (newBalance) => {
    setUser(prev => prev ? ({ ...prev, balance: newBalance }) : prev);
  };

  const updateBalances = ({ balance, fuzzy_balance }) => {
    setUser(prev => prev ? ({
      ...prev,
      ...(balance !== undefined ? { balance } : {}),
      ...(fuzzy_balance !== undefined ? { fuzzy_balance } : {}),
    }) : prev);
  };

  const refreshUser = async () => {
    if (token) {
      await fetchUserProfile(token);
    }
  };

  return (
    <AuthContext.Provider value={{
      user, token, loading, login, register, logout,
      updateBalance, updateBalances, refreshUser,
      isAuthenticated: !!token && !!user,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
